import React, { useRef, useEffect, useState } from "react";
import "../Fees/Fees.css";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import axios from "../../utils/axiosConfig";

// SUPERSCRIPT
const formatSuperscript = (text) => {
  if (!text) return "";
  return text.replace(
    /\b(\d+)(st|nd|rd|th)\b/g,
    (_, num, suffix) => `${num}<sup>${suffix}</sup>`
  );
};

// ICON LOGIC
const getRemarkIcon = (remark) => {
  const text = remark.toLowerCase();

  if (text.includes("fine") || text.includes("penalty")) {
    return "bi bi-exclamation-triangle-fill text-warning";
  } else if (text.includes("strike off") || text.includes("not allowed") || text.includes("sent home")) {
    return "bi bi-x-circle-fill text-danger";
  } else if (text.includes("payable") || text.includes("fees") || text.includes("deposit")) {
    return "bi bi-cash-stack text-success";
  } else if (text.includes("exam")) {
    return "bi bi-journal-text text-primary";
  } else if (text.includes("increase") || text.includes("annual")) {
    return "bi bi-graph-up-arrow text-info";
  }
  return "bi bi-info-circle-fill text-secondary";
};

// CATEGORY
const categorizeRemark = (remark) => {
  const text = remark.toLowerCase();

  if (text.includes("payable") || text.includes("fees") || text.includes("deposit")) {
    return "Payment Rules";
  } else if (text.includes("fine") || text.includes("penalty") || text.includes("strike off") || text.includes("sent home")) {
    return "Penalties";
  } else if (text.includes("exam")) {
    return "Exam Rules";
  } else if (text.includes("increase") || text.includes("annual")) {
    return "General Policies";
  }
  return "Other";
};

const Fees = () => {
  const printRef = useRef();

  const [meta, setMeta] = useState({});
  const [monthlyFees, setMonthlyFees] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [rules, setRules] = useState([]);
  const [feeTop, setFeeTop] = useState([]);

  const API = "/api/fees";

  // LOAD DATA
  useEffect(() => {
    axios.get(`${API}/meta`).then(res => setMeta(res.data));
    axios.get(`${API}/monthly`).then(res => setMonthlyFees(res.data));
    axios.get(`${API}/schedule`).then(res => setSchedule(res.data));
    axios.get(`${API}/rules`).then(res => setRules(res.data));
    axios.get(`${API}/top`).then(res => setFeeTop(res.data)).catch(()=>{});
  }, []);

  // PDF
  const handlePDF = async () => {
    const element = printRef.current;
    const canvas = await html2canvas(element, { scale: 2 });
    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF("p", "mm", "a4");
    const margin = 10;
    const width = 210 - margin * 2;
    const height = (canvas.height * width) / canvas.width;

    pdf.addImage(imgData, "PNG", margin, margin, width, height);
    pdf.save("Fees-Structure.pdf");
  };

  const total = feeTop.reduce((a, b) => a + Number(b.amount || 0), 0);

  return (
    <div className="fees-container">

      <div ref={printRef}>

        {/* BANNER */}
        <div className="fees-banner">
          <h1 className="fees-banner-title">
            School Fee Rules & Regulation
          </h1>
          <p className="fees-banner-subtitle">
            Important information about school fee structure and policies
          </p>
        </div>

        {/* PDF BUTTON */}
        <div className="fees-actions">
          <button onClick={handlePDF}>Export PDF</button>
        </div>

        {/* CHART */}
        <div className="fees-chart-card">

          <h2 className="fees-main-title">
            FEE STRUCTURE : P.G. - CLASS XII
          </h2>

          <div className="fees-top">
            {feeTop.map((item, i) => (
              <div key={i}>
                {item.label} : ₹ {item.amount} P.A.
              </div>
            ))}
            <div className="fees-total">
              TOTAL : ₹ {total} (To be deposited in April)
            </div>
          </div>

          <div className="fees-table-wrap">

            {/* MONTHLY */}
            <table className="fees-table">
              <thead>
                <tr>
                  <th>MONTHLY FEE</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {monthlyFees.map((row, i) => (
                  <tr key={i}>
                    <td>{row.class_name}</td>
                    <td>₹ {row.amount} PM</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* SCHEDULE */}
            <table className="fees-table">
              <thead>
                <tr>
                  <th>Fee for the month</th>
                  <th>Fees to be paid by</th>
                </tr>
              </thead>
              <tbody>
                {schedule.map((row, i) => (
                  <tr key={i}>
                    <td>{row.month_name}</td>
                    <td
                      dangerouslySetInnerHTML={{
                        __html: formatSuperscript(row.due_date)
                      }}
                    />
                  </tr>
                ))}
              </tbody>
            </table>

          </div>

          <div className="fees-foot">
            All fees & Bus Charges are payable for 12 months
            <br />
            <span>Late Fee For Previous Month Rs. 200.00 per month</span>
          </div>

        </div>

        {/* RULES */}
        <div className="fees-rules">

          <div className="fees-card">
            <h3>{meta.feetitle}</h3>
            <p>{meta.feerules}</p>

            {(() => {
              const grouped = rules.reduce((acc, r) => {
                const cat = categorizeRemark(r.rule_text);
                if (!acc[cat]) acc[cat] = [];
                acc[cat].push(r.rule_text);
                return acc;
              }, {});

              return Object.keys(grouped).map((cat) => (
                <div key={cat}>
                  <h4>{cat}</h4>
                  <ul>
                    {grouped[cat].map((r, i) => (
                      <li key={i}>
                        <i className={getRemarkIcon(r)}></i>
                        <span
                          dangerouslySetInnerHTML={{
                            __html: formatSuperscript(r)
                          }}
                        />
                      </li>
                    ))}
                  </ul>
                </div>
              ));
            })()}

          </div>

        </div>

      </div>
    </div>
  );
};

export default Fees;